import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchShowProject } from "../../features/ProjectSlice/ShowProjectSlice";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";

// Gallery Modal Component
function GalleryModal({ images, currentIndex, onClose, onChangeIndex }) {
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const handleNext = () => {
    onChangeIndex((currentIndex + 1) % images.length);
  };

  const handlePrev = () => {
    onChangeIndex((currentIndex - 1 + images.length) % images.length);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100]">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative max-w-6xl w-full"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white text-5xl z-10 hover:text-red-500 transition-colors"
        >
          &times;
        </button>

        {/* Navigation Buttons */}
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl z-10 hover:text-emerald-500 transition-colors"
        >
          &larr;
        </button>

        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl z-10 hover:text-emerald-500 transition-colors"
        >
          &rarr;
        </button>

        {/* Main Image */}
        <img
          src={images[currentIndex]}
          alt={`Gallery item ${currentIndex + 1}`}
          className="max-h-[90vh] max-w-full mx-auto object-contain"
        />

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-lg bg-black bg-opacity-50 px-3 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>
      </motion.div>
    </div>
  );
}

// Main Component
function ShowProject() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { project } = useSelector((state) => state.showProject);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    dispatch(fetchShowProject(id));
  }, [id, dispatch, i18n.language]);

  // Prepare all images array
  const allImages = [
    project?.image,
    ...(project?.images || [])
  ].filter(Boolean).map(img => process.env.REACT_APP_IMG_URL + img);

  // Open modal at specific index
  const openModalAtIndex = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="my-8 px-4 lg:px-0 lg:w-10/12 mx-auto"
    >
      {/* Gallery Modal */}
      {isModalOpen && (
        <GalleryModal
          images={allImages}
          currentIndex={currentImageIndex}
          onClose={() => setIsModalOpen(false)}
          onChangeIndex={setCurrentImageIndex}
        />
      )}

      {/* Title Section */}
      <motion.div variants={childVariants} className="w-full">
        <h1 className={`${i18n.language === "en" || i18n.language === "tr"
          ? "border-l-4" : "border-r-4"} px-3 border-emerald-600 text-3xl md:text-4xl font-bold text-gray-800`}>
          {project?.title}
        </h1>
      </motion.div>

      <motion.div
        variants={childVariants}
        className={`h-0.5 w-full my-6 ${i18n.language === "en" || i18n.language === "tr"
          ? "bg-gradient-to-r from-emerald-500 to-transparent"
          : "bg-gradient-to-l from-emerald-500 to-transparent"
          }`}
      />

      {/* Content Section */}
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Text Content */}
        <motion.div variants={childVariants} className="w-full lg:w-1/2 space-y-6 order-2 lg:order-1">
          <div
            dir="auto"
            style={{ textAlign: 'justify', textJustify: 'inter-word', hyphens: 'auto' }}
            className="prose dark:prose-invert max-w-none text-gray-900 text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: project?.description }}
          />
        </motion.div>


        {/* Main Image */}
        <motion.div variants={childVariants} style={{ aspectRatio: '16/9' }} className="w-full lg:w-1/2 order-1 lg:order-2 shadow-md rounded-md ">
          <div
            className="shadow-md rounded-xl "
            onClick={() => openModalAtIndex(0)}
          >
            <img
              style={{ aspectRatio: '16/9' }}
              className="w-full h-full object-cover rounded-md cursor-pointer hover:scale-105 transition-transform duration-300"
              src={process.env.REACT_APP_IMG_URL + project?.image}
              alt={project?.title}
            />
          </div>
        </motion.div>
      </div>

      {/* Image Gallery Grid */}
      {project?.images?.length > 0 && (
        <div className="grid grid-cols-1  md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 my-8 gap-4">
          {project.images.map((image, index) => (
            <motion.div
              variants={childVariants}
              key={image}
              onClick={() => openModalAtIndex(index + 1)}
              className="cursor-pointer"
            >
              <div className="relative overflow-hidden flex justify-center  transition-shadow duration-300">
                <LazyLoadImage

                  style={{ aspectRatio: '16/9' }}
                  className="w-11/12 lg:w-full h-40 md:h-48 lg:h-52 object-cover rounded-xl transform hover:scale-105 transition-transform duration-300"
                  src={process.env.REACT_APP_IMG_URL + image}
                  alt={`${project.title} - ${index + 1}`}
                />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Attachment Section */}
      {project?.attachment && (
        <div className="relative w-full mt-3 max-w-4xl mx-auto overflow-hidden rounded-2xl border border-gray-200 shadow-lg bg-gray-100 transition-all duration-300 hover:shadow-2xl">
          <div className="relative pt-[56.25%]">
            <iframe
              src={project.attachment}
              className="absolute top-0 left-0 w-full h-full rounded-2xl"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default ShowProject;